/**
 * featureEvents — the browser's side of the feature log (clarab/config/featureEvents.js is the catalog,
 * POST /api/feature-events takes the rows, /qa-manager/acp-relay shows them under ACP · Clara AI ·
 * Build a tool).
 *
 *   trackFeatureEvent('acp.section.open', { target: 'bonuses', market: 'com', meta: { via: 'rail' } })
 *
 * Events queue up and go out in one request every 1.5 s (≤ 50 per request); when the tab is hidden or
 * closed the queue is flushed with a keepalive fetch so the last clicks are not lost. The server stamps
 * the caller and the page (X-Clara-Page rides on every request via the axios interceptor; for the
 * keepalive fetch we add it ourselves) and refuses anything that is not a client event of the
 * catalog — so this file can only ever say WHAT happened, never WHO.
 *
 * Never throws, never blocks, and does nothing without a token or a window (tests).
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const FLUSH_MS = 1500;
const MAX_BATCH = 50;
const MAX_QUEUE = 500;

let queue = [];
let timer = null;
let listening = false;

const token = () => { try { return localStorage.getItem('token'); } catch { return null; } };

/**
 * The page a request is filed under — X-Clara-Page here and in index.js's axios interceptor, which
 * AcpUsageLog / AcpUsageStat (one row per page per day, kept forever) and this log store. A tool's
 * link carries the searched player (/tools/<tool>/<userId>, /tools/my/<id>/<userId> — utils/dockEmbed.js);
 * the logs count tools, not players, so that segment is dropped. The builder keeps its tool id.
 */
export function loggedPage(pathname) {
  const s = String(pathname || '').split('/');
  const keep = s[1] !== 'tools' || s[2] === 'builder' ? s.length : (s[2] === 'my' ? 4 : 3);
  return s.slice(0, keep).join('/').slice(0, 200);
}
const page = () => { try { return loggedPage(window.location.pathname); } catch { return null; } };

function schedule() {
  if (timer || typeof window === 'undefined') return;
  timer = window.setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

async function flush({ keepalive = false } = {}) {
  if (!queue.length) return;
  const t = token();
  if (!t) { queue = []; return; }
  const batch = queue.splice(0, MAX_BATCH);
  const body = { events: batch };
  try {
    if (keepalive && typeof fetch === 'function') {
      await fetch(`${API_URL}/api/feature-events`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'X-Clara-Page': page() || '' }, body: JSON.stringify(body) });
    } else {
      await axios.post(`${API_URL}/api/feature-events`, body, { headers: { Authorization: `Bearer ${t}` }, timeout: 8000 });
    }
  } catch { /* a lost log row is not worth a retry loop */ }
  if (queue.length) schedule();
}

function listen() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { if (timer) { clearTimeout(timer); timer = null; } flush({ keepalive: true }); } });
  window.addEventListener('pagehide', () => { if (timer) { clearTimeout(timer); timer = null; } flush({ keepalive: true }); });
}

/**
 * Queue one client event. `event` must be a client key of the catalog (anything else is dropped by the
 * server); `target` is the thing it concerns (a section id, a tool id…), `meta` a small plain object.
 */
export function trackFeatureEvent(event, { target = null, market = null, meta = null, label = null, page: pageOverride = null } = {}) {
  try {
    if (typeof window === 'undefined' || typeof event !== 'string' || !event) return;
    if (!token()) return;
    if (queue.length >= MAX_QUEUE) queue.shift();
    const row = { event, at: new Date().toISOString() };
    if (target != null) row.target = String(target).slice(0, 300);
    if (market) row.market = String(market).slice(0, 12);
    if (label) row.label = String(label).slice(0, 300);
    if (meta && typeof meta === 'object') row.meta = meta;
    if (pageOverride) row.page = String(pageOverride).slice(0, 200);
    queue.push(row);
    listen();
    schedule();
  } catch { /* never let telemetry break the page */ }
}

/** Send whatever is queued now (tests, or right before a hard navigation). */
export function flushFeatureEvents() { if (timer) { clearTimeout(timer); timer = null; } return flush(); }

/** For tests. */
export function _pendingFeatureEvents() { return queue.slice(); }
export function _resetFeatureEvents() { queue = []; if (timer) { clearTimeout(timer); timer = null; } }
